/**
 * Composable para formatadores comuns
 */
export function useFormatters() {
  /**
   * Formata um valor para moeda brasileira
   */
  function formatCurrency(value: number): string {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value)
  }

  /**
   * Formata uma data para o padrão brasileiro
   */
  function formatDate(date: string | Date, options?: Intl.DateTimeFormatOptions): string {
    const dateObj = typeof date === 'string' ? new Date(date) : date
    
    const defaultOptions: Intl.DateTimeFormatOptions = {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      ...options
    }
    
    return dateObj.toLocaleDateString('pt-BR', defaultOptions)
  }

  /**
   * Formata uma data com hora
   */
  function formatDateTime(date: string | Date): string {
    const dateObj = typeof date === 'string' ? new Date(date) : date
    
    return dateObj.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  /**
   * Formata uma data relativa (ex: "há 2 dias")
   */
  function formatRelativeDate(date: string | Date): string {
    const dateObj = typeof date === 'string' ? new Date(date) : date
    const now = new Date()
    const diffMs = now.getTime() - dateObj.getTime()
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    const diffMinutes = Math.floor(diffMs / (1000 * 60))

    if (diffMinutes < 1) return 'agora mesmo'
    if (diffMinutes < 60) return `há ${diffMinutes} minuto${diffMinutes > 1 ? 's' : ''}`
    if (diffHours < 24) return `há ${diffHours} hora${diffHours > 1 ? 's' : ''}`
    if (diffDays < 7) return `há ${diffDays} dia${diffDays > 1 ? 's' : ''}`
    if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7)
      return `há ${weeks} semana${weeks > 1 ? 's' : ''}`
    }
    
    return formatDate(dateObj)
  }

  /**
   * Formata um número de telefone
   */
  function formatPhone(phone: string): string {
    const cleaned = phone.replace(/\D/g, '')
    
    if (cleaned.length === 11) {
      return cleaned.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3')
    } else if (cleaned.length === 10) {
      return cleaned.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3')
    }
    
    return phone
  }

  /**
   * Formata um CNPJ
   */
  function formatCNPJ(cnpj: string): string {
    const cleaned = cnpj.replace(/\D/g, '')
    return cleaned.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')
  }

  /**
   * Formata um CPF
   */
  function formatCPF(cpf: string): string {
    const cleaned = cpf.replace(/\D/g, '')
    return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
  }

  /**
   * Formata um CEP
   */
  function formatCEP(cep: string): string {
    const cleaned = cep.replace(/\D/g, '')
    return cleaned.replace(/(\d{5})(\d{3})/, '$1-$2')
  }

  /**
   * Trunca um texto
   */
  function truncate(text: string, length: number, suffix: string = '...'): string {
    if (text.length <= length) return text
    return text.substring(0, length).trim() + suffix
  }

  /**
   * Converte segundos para formato de duração
   */
  function formatDuration(seconds: number): string {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const secs = seconds % 60

    if (hours > 0) {
      return `${hours}h ${minutes}min`
    } else if (minutes > 0) {
      return `${minutes}min ${secs}s`
    } else {
      return `${secs}s`
    }
  }

  /**
   * Gera iniciais de um nome
   */
  function getInitials(name: string): string {
    return name
      .split(' ')
      .map(word => word.charAt(0).toUpperCase())
      .slice(0, 2)
      .join('')
  }

  /**
   * Formata bytes para tamanho legível
   */
  function formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes'
    
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  return {
    formatCurrency,
    formatDate,
    formatDateTime,
    formatRelativeDate,
    formatPhone,
    formatCNPJ,
    formatCPF,
    formatCEP,
    truncate,
    formatDuration,
    getInitials,
    formatFileSize,
  }
}
